import React from 'react';
import { Box, LinearProgress, Typography, Paper, Button } from '@mui/material';
import { DownloadProgress as ProgressType } from '../services/S3DataService';

interface DownloadProgressProps {
  progress: ProgressType;
  onCancel?: () => void;
  onRetry?: () => void;
  onDismissError?: () => void;
}

export const DownloadProgress: React.FC<DownloadProgressProps> = ({
  progress,
  onCancel,
  onRetry,
  onDismissError
}) => {
  const { status, percentage, completed, total, error } = progress;

  if (status === 'idle') {
    return null;
  }

  let statusText = '';
  let isS3Error = false;

  switch (status) {
    case 'downloading':
      statusText = 'Downloading data...';
      break;
    case 'processing':
      statusText = 'Processing data...';
      break;
    case 'complete':
      statusText = 'Download complete!';
      break;
    case 'error':
      // Check if this is an S3-related error
      isS3Error =
        error?.includes('S3') ||
        error?.includes('download') ||
        error?.includes('fetch') ||
        error?.includes('CSV') ||
        error?.includes('presigned') ||
        false;

      // Provide a more user-friendly error message for S3 errors
      if (isS3Error) {
        statusText =
          'Failed to fetch data from S3. This could be due to network issues or the data may not be available.';
      } else {
        statusText = `Error: ${error || 'Unknown error'}`;
      }
      break;
  }

  return (
    <Paper
      elevation={3}
      sx={{
        p: 2,
        mb: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1
      }}
    >
      <Typography variant="subtitle1" fontWeight="bold">
        {statusText}
      </Typography>

      <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ flexGrow: 1 }}>
          <LinearProgress
            variant="determinate"
            value={percentage}
            color={status === 'error' ? 'error' : 'primary'}
          />
        </Box>
        <Typography variant="body2" color="text.secondary">
          {percentage}%
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary">
        {completed} of {total} files {status === 'complete' ? 'downloaded' : 'downloading'}
      </Typography>

      {/* Action buttons based on status */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, gap: 1 }}>
        {/* Cancel button for in-progress downloads */}
        {(status === 'downloading' || status === 'processing') && onCancel && (
          <Button size="small" color="secondary" onClick={onCancel}>
            Cancel
          </Button>
        )}

        {/* Retry button for errors */}
        {status === 'error' && onRetry && (
          <Button size="small" color="primary" onClick={onRetry} variant="contained">
            Retry Download
          </Button>
        )}

        {/* Dismiss button for errors */}
        {status === 'error' && onDismissError && (
          <Button size="small" color="secondary" onClick={onDismissError}>
            Dismiss
          </Button>
        )}
      </Box>
    </Paper>
  );
};
